import React, { useEffect, useRef, useState } from 'react';

function useSyncedText(items, serialize, parse, onChange) {
  const [text, setText] = useState(() => serialize(items));
  const lastEmitted = useRef(items);

  useEffect(() => {
    if (items !== lastEmitted.current) setText(serialize(items));
  }, [items, serialize]);

  const updateText = (value) => {
    setText(value);
    const nextItems = parse(value);
    lastEmitted.current = nextItems;
    onChange(nextItems);
  };

  return [text, updateText];
}

const serializeSpecifications = (rows) => rows.map((row) => `${row.title}: ${row.component}`).join('\n');
const parseSpecifications = (text) => text.split(/\r?\n/).flatMap((line) => {
  const separatorIndex = line.search(/[:\t]/);
  if (separatorIndex < 1) return [];
  const title = line.slice(0, separatorIndex).trim();
  const component = line.slice(separatorIndex + 1).trim();
  return title && component ? [{ title, component }] : [];
}).slice(0, 50);

const serializePoints = (points) => points.join('\n');
const parsePoints = (text) => text.split(/\r?\n/).map((point) => point.trim()).filter(Boolean).slice(0, 50);

export default function ProductDetailRowsEditor({ rows, onChange, idPrefix }) {
  const [text, setText] = useSyncedText(rows, serializeSpecifications, parseSpecifications, onChange);

  return (
    <div className="va-bulk-editor">
      <label htmlFor={`${idPrefix}-bulk`}>One specification per line</label>
      <textarea
        id={`${idPrefix}-bulk`}
        className="va-textarea va-bulk-editor__textarea"
        value={text}
        placeholder={'Model: M10A\nProduct Type: 10.1" Rugged Tablet / Rugged Mobile PC\nOperating System: Windows 11'}
        onChange={(event) => setText(event.target.value)}
      />
      <small>Use the format <strong>Title: Component</strong>. You can paste an entire specification list at once.</small>
      <span>{rows.length} valid specification{rows.length === 1 ? '' : 's'}</span>
    </div>
  );
}

export function ProductSellingPointsEditor({ points, onChange, idPrefix }) {
  const [text, setText] = useSyncedText(points, serializePoints, parsePoints, onChange);

  return (
    <div className="va-bulk-editor">
      <label htmlFor={`${idPrefix}-bulk`}>One selling point per line</label>
      <textarea
        id={`${idPrefix}-bulk`}
        className="va-textarea va-bulk-editor__textarea"
        value={text}
        placeholder={'Hot-swappable battery for uninterrupted work\nIP65 and MIL-STD-810H rugged protection\nWi-Fi 6E and optional 4G connectivity'}
        onChange={(event) => setText(event.target.value)}
      />
      <small>No title is needed. Each non-empty line becomes one key selling point.</small>
      <span>{points.length} selling point{points.length === 1 ? '' : 's'}</span>
    </div>
  );
}
